import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import PasswordStrength, { validatePassword } from '../components/PasswordStrength';

export default function ProfilePage() {
  const toast = useToast();
  const { user, refreshUser } = useAuth();
  const [profileForm, setProfileForm] = useState({ full_name:'', email:'', avatar_url:'' });
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [show, setShow] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (user) setProfileForm({ full_name: user.full_name||'', email: user.email||'', avatar_url: user.avatar_url||'' });
  }, [user]);

  const handleAvatar = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { toast('Image must be under 2MB', 'error'); return; }
    const reader = new FileReader();
    reader.onload = () => setProfileForm(f => ({ ...f, avatar_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.post('/users/update_profile.php', profileForm);
      if (data.success) { toast('Profile updated!'); refreshUser(); }
      else toast(data.error, 'error');
    } finally { setSavingProfile(false); }
  };

  const changePw = async () => {
    const err = validatePassword(pwForm.new_password);
    if (err) { toast(err, 'error'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast('Passwords do not match', 'error'); return; }
    setSavingPw(true);
    try {
      const { data } = await api.post('/auth/change_password.php', pwForm);
      if (data.success) { toast('Password changed!'); setPwForm({ current_password:'', new_password:'', confirm_password:'' }); }
      else toast(data.error, 'error');
    } finally { setSavingPw(false); }
  };

  const initials = (user?.full_name||user?.username||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
  const toggleShow = k => () => setShow(s => ({ ...s, [k]: !s[k] }));

  const PwField = ({ k, label, placeholder }) => (
    <div>
      <label className="lbl">{label}</label>
      <div style={{ position:'relative' }}>
        <input className="input" type={show[k]?'text':'password'} value={pwForm[k]} onChange={e => setPwForm(f=>({...f,[k]:e.target.value}))} placeholder={placeholder} style={{ paddingRight:40 }} />
        <button type="button" onClick={toggleShow(k)} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer' }}>{show[k]?'🙈':'👁'}</button>
      </div>
    </div>
  );

  if (!user) return null;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">My Profile</div><div className="page-sub">Update your personal details and change your password</div></div>
      </div>
      <div className="page-body">
        <div style={{ maxWidth:640, display:'flex', flexDirection:'column', gap:18 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">👤 Profile Information</span></div>
            <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:18 }}>
              <div style={{ display:'flex', alignItems:'center', gap:18 }}>
                <div style={{ position:'relative' }}>
                  <div style={{ width:78, height:78, borderRadius:'50%', background:'var(--teal-light)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26, fontWeight:700, color:'var(--teal)', overflow:'hidden', border:'4px solid #fff', boxShadow:'var(--shadow)' }}>
                    {profileForm.avatar_url ? <img src={profileForm.avatar_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials}
                  </div>
                  <button onClick={() => fileRef.current?.click()} style={{ position:'absolute', bottom:-2, right:-2, width:26, height:26, borderRadius:'50%', background:'var(--teal)', border:'2px solid #fff', color:'#fff', cursor:'pointer', fontSize:11 }}>✏️</button>
                  <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleAvatar} />
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:17 }}>{user.full_name||user.username}</div>
                  <span className={`badge ${user.role==='admin'?'badge-purple':'badge-slate'}`} style={{ marginTop:4 }}>{user.role}</span>
                  <div style={{ fontSize:11, color:'var(--text2)', marginTop:4 }}>Last login: {user.last_login ? new Date(user.last_login).toLocaleString('en-GB') : 'Never'}</div>
                </div>
              </div>
              <div className="form-grid cols-2">
                <div><label className="lbl">Username (read-only)</label><input className="input" readOnly value={user.username} /></div>
                <div><label className="lbl">Role (read-only)</label><input className="input" readOnly value={user.role} /></div>
                <div><label className="lbl">Full Name</label><input className="input" value={profileForm.full_name} onChange={e => setProfileForm(f=>({...f,full_name:e.target.value}))} /></div>
                <div><label className="lbl">Email</label><input type="email" className="input" value={profileForm.email} onChange={e => setProfileForm(f=>({...f,email:e.target.value}))} /></div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:10, borderTop:'1px solid var(--border)' }}>
                <button className="btn btn-primary" onClick={saveProfile} disabled={savingProfile}>{savingProfile?'Saving…':'Save Profile'}</button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">🔒 Change Password</span></div>
            <div className="card-body" style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <PwField k="current_password" label="Current Password" placeholder="Your current password" />
              <div>
                <PwField k="new_password" label="New Password" placeholder="New password" />
                <PasswordStrength password={pwForm.new_password} />
              </div>
              <div>
                <PwField k="confirm_password" label="Confirm New Password" placeholder="Confirm new password" />
                {pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password && <p style={{ fontSize:11, color:'var(--danger)', marginTop:4 }}>Passwords do not match</p>}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:10, borderTop:'1px solid var(--border)' }}>
                <button className="btn btn-primary" onClick={changePw} disabled={savingPw}>{savingPw?'Changing…':'Change Password'}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
